import type { Rows, Workbook, Worksheet } from 'exceljs';
import moment from 'moment-timezone';

import type { TimestampInMillis, Vehicle } from '@cobliteam/common-types';

import { type NewActivity, type NewRoute, RouteType, type TimeWindow } from '@cobliteam/api-lib';

import { activityTimeCheck } from '../components/ActivityBreak/utils';

import { formatDistance, formatDuration, getFormattedAddress, millisToDate, millisToHour } from '../utils';

const FIRST_ROW_AFTER_TEMPLATE_HEADER = 7;

export class RouteExportBuilder {
  private routeType = (type: RouteType) => {
    switch (type) {
      case RouteType.MANUAL:
        return 'ROTA MANUAL';
      case RouteType.OPTIMIZED:
        return 'ROTA OTIMIZADA';
    }
  };

  private routeInfo = (worksheet: Worksheet, route: NewRoute, vehicles: Vehicle[]) => {
    const vehicle = vehicles.find(({ id }) => id === route.vehicleId);
    const vehicleInfo = vehicle && (vehicle.licensePlate || vehicle.cobliId);
    const driverInfo = route.driverName ? `- ${route.driverName}` : ' ';
    const vehicleAlocated = vehicle ? `${vehicleInfo} ${driverInfo}` : 'Sem veículo associado';

    worksheet.getRow(1).getCell(1).value = this.routeType(route.type);
    worksheet.getRow(3).getCell(1).value = route.name;
    worksheet.getRow(3).getCell(2).value = vehicleAlocated;
    worksheet.getRow(3).getCell(3).value = millisToDate(route.startTime);
    worksheet.getRow(3).getCell(4).value = formatDistance(route.plannedDistanceInMeters);
    worksheet.getRow(3).getCell(5).value = formatDuration(route.plannedDuration);
  };

  private timeWindow = (timeWindows?: TimeWindow[]) => (timeWindows ? `${millisToHour(timeWindows[0].earliest)} - ${millisToHour(timeWindows[0].latest)}` : '-');

  private plannedDuration = (plannedDuration?: TimestampInMillis) => (plannedDuration ? formatDuration(plannedDuration) : '-');

  private diffTime = (nextActivity?: NewActivity, startTime?: TimestampInMillis) => {
    if (nextActivity) {
      const diffTime = moment(nextActivity.startTime).diff(moment(startTime));

      return formatDuration(diffTime);
    }
    return '-';
  };

  private rowTransformer = (activity: NewActivity, index: number, nextActivity: NewActivity) => {
    const { name, destination, additionalInfo, startTime, timeWindows, plannedDuration } = activity;

    return [
      index,
      name?.length ? name : '-',
      getFormattedAddress(destination),
      additionalInfo,
      `${millisToHour(startTime)} ${index === 0 ? '(início)' : ''}`,
      this.timeWindow(timeWindows),
      this.plannedDuration(plannedDuration),
      this.diffTime(nextActivity, startTime),
    ];
  };

  private rowsActivitiesTransformer = (route: NewRoute) => {
    const rows: Rows[] = [];

    route.activities.forEach((activity, index, activities) => {
      const nextActivity = activities[index + 1];
      const breakTime = activityTimeCheck(route, activity, nextActivity);

      rows.push(this.rowTransformer(activity, index, nextActivity));

      if (breakTime) {
        rows.push(['Intervalo', `${millisToHour(route.break?.startTime)} até ${millisToHour(route.break?.endTime)}`]);
      }
    });

    return rows;
  };

  private readFile = (fileRes: Blob): Promise<Buffer> =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(fileRes);
      reader.onload = () => {
        resolve(reader.result as Buffer);
      };
    });

  public buildReport = async (spreadsheetTemplate: Blob, route: NewRoute, vehicles: Vehicle[]) => {
    const buffer = await this.readFile(spreadsheetTemplate);

    const ExcelJS = await import('exceljs');
    const workbook = new ExcelJS.Workbook();

    const file: Workbook = await workbook.xlsx.load(buffer);
    file.creator = 'Cobli';

    const sheet: any = file.getWorksheet(1);
    sheet.pageSetup.showGridLines = true;

    this.routeInfo(sheet, route, vehicles);

    const insertedRows = sheet.insertRows(FIRST_ROW_AFTER_TEMPLATE_HEADER, this.rowsActivitiesTransformer(route));

    if (insertedRows) {
      insertedRows.forEach((row: any) => {
        row.getCell(2).alignment = { wrapText: true };
        row.getCell(3).alignment = { wrapText: true };
        row.getCell(4).alignment = { wrapText: true };
        row.height = 45;

        row.eachCell({ includeEmpty: true }, (cell: any) => {
          if (cell.value === 'Intervalo') {
            row.fill = {
              type: 'pattern',
              pattern: 'lightUp',
              fgColor: { argb: 'dcffb9' },
            };
            row.alignment = {
              vertical: 'middle',
              horizontal: 'center',
            };
          }

          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          };
        });
      });
    }

    sheet.name = route.name.replace(/[&/\\#, +()$~%.'":*?<>{}]/g, '_');
    sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
    sheet.getRow(2).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };
    sheet.getRow(3).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };
    sheet.getRow(6).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true,
    };

    const excelBuffer = await workbook.xlsx.writeBuffer();

    return new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
  };
}
